import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    BookOpen, PenLine, StickyNote, Heart, Target, HelpCircle,
    ChevronRight, Droplet, ArrowRight,
} from "lucide-react";
import {
    listGoals, listSubjects, fetchQuestionOfTheDay,
} from "@/lib/api";
import {
    scheduledSlotsForToday, legacyRhythmConfig, fmtTime, todayKey,
    formatHorizon, daysUntil,
} from "@/components/goals/helpers";
import axios from "axios";

const READER_POSITION_KEY = "matthew-reader-position";
const READER_VERSION_KEY = "matthew-reader-version";
const DISPLAY_NAME_KEY = "display-name";

// ---------- helpers ----------

function greetingWord(hour) {
    if (hour < 5) return "Good evening";
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
}

function readerLastPosition() {
    try {
        const raw = localStorage.getItem(READER_POSITION_KEY);
        const saved = raw ? JSON.parse(raw) : {};
        const chapter = Number(saved.chapter) || 1;
        return chapter;
    } catch (_e) { return 1; }
}
function readerVersion() {
    try {
        return (localStorage.getItem(READER_VERSION_KEY) || "kjv").toLowerCase();
    } catch (_e) { return "kjv"; }
}
function displayName() {
    try {
        return (localStorage.getItem(DISPLAY_NAME_KEY) || "").trim();
    } catch (_e) { return ""; }
}

// A "reading goal" is a standard goal whose title looks like a reading plan.
// Heuristic: category Walking with God + title mentions "Read".
function pickReadingGoal(goals) {
    return goals.find(g =>
        g.status === "active" &&
        g.goal_type !== "quantity" &&
        /\bread\b/i.test(g.title || "")
    ) || null;
}

// ---------- section: greeting ----------

function Greeting() {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 60_000);
        return () => clearInterval(t);
    }, []);
    const name = displayName();
    const g = greetingWord(now.getHours());
    const dateStr = now.toLocaleDateString(undefined, {
        weekday: "long", month: "long", day: "numeric",
    });
    return (
        <header data-testid="home-greeting" className="mb-6">
            <h1 className="scripture text-[30px] md:text-[36px] leading-tight text-ink tracking-tight">
                {g}{name ? `, ${name}` : ""}
            </h1>
            <p className="mt-1 font-ui text-[13px] text-ink-40">{dateStr}</p>
        </header>
    );
}

// ---------- section: invitations ----------

// Returns up to two invitations: [practice?, goal?]
function useInvitations(goals, goalsLoaded) {
    const [logsByGoal, setLogsByGoal] = useState(null); // null = not-loaded yet
    const activeQuantityGoals = useMemo(() =>
        goals.filter(g => g.status === "active" && g.goal_type === "quantity"),
    [goals]);
    useEffect(() => {
        if (!goalsLoaded) return;
        if (activeQuantityGoals.length === 0) { setLogsByGoal({}); return; }
        let cancelled = false;
        Promise.all(activeQuantityGoals.map(g =>
            axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/goals/${g.id}/logs`, {
                params: { date: todayKey() },
            }).then(r => [g.id, r.data.logs || []]).catch(() => [g.id, []])
        )).then(pairs => {
            if (cancelled) return;
            const m = {};
            pairs.forEach(([id, logs]) => { m[id] = logs; });
            setLogsByGoal(m);
        });
        return () => { cancelled = true; };
    }, [activeQuantityGoals, goalsLoaded]);

    return useMemo(() => {
        // Hold render until both goals and their logs have loaded — otherwise the
        // quantity CTA flashes ('Today' → 'N unit today') as data streams in.
        if (!goalsLoaded || logsByGoal === null) return null;

        const invitations = [];
        // Spec: at most one practice card + at most one goal-thing (slot or quantity),
        // giving a hard max of two on Home. Practice system is not built yet, so today
        // we surface only the first eligible goal invitation.
        for (const g of goals) {
            if (invitations.some(x => x.kind === "goal")) break;
            if (g.status !== "active") continue;
            if (g.goal_type === "quantity") {
                const target = Number((g.quantity_config || {}).target_per_day) || 0;
                const unit = (g.quantity_config || {}).unit || "";
                const logs = logsByGoal[g.id] || [];
                const total = logs.reduce((s, l) => s + Number(l.amount || 0), 0);
                if (target > 0 && total < target) {
                    invitations.push({
                        kind: "goal",
                        subkind: "quantity",
                        id: g.id,
                        label: g.title,
                        cta: total > 0 ? `${total} ${unit} today` : `Today`,
                        icon: (g.quantity_config || {}).icon === "water" ? Droplet : Target,
                        to: `/goals/${g.id}`,
                    });
                }
                continue;
            }
            const cfg = legacyRhythmConfig(g);
            const todaysSlots = scheduledSlotsForToday(cfg);
            if (!todaysSlots.length) continue;
            const nextSlot = todaysSlots[0];
            invitations.push({
                kind: "goal",
                subkind: "slot",
                id: g.id,
                label: g.title,
                cta: `${nextSlot.label || fmtTime(nextSlot.time)} — check in`,
                icon: Target,
                to: `/goals/${g.id}`,
            });
        }
        return invitations;
    }, [goals, goalsLoaded, logsByGoal]);
}

function Invitations({ goals, goalsLoaded }) {
    const invitations = useInvitations(goals, goalsLoaded);
    // Hidden while loading OR when nothing is due.
    if (!invitations || invitations.length === 0) return null;
    return (
        <section data-testid="home-invitations" className="mb-6">
            <h2 className="font-ui text-[10px] uppercase tracking-[0.22em] text-ink-40 font-semibold mb-2">
                Today
            </h2>
            <div className="grid gap-3">
                {invitations.map((inv, i) => (
                    <Link
                        key={inv.id + i}
                        to={inv.to}
                        data-testid={`invitation-${inv.subkind}`}
                        className="block p-4 rounded-3xl bg-white border border-moss/30 shadow-sm hover:shadow-md hover:border-moss/60 transition"
                        style={{ background: "linear-gradient(to right, rgba(183,205,177,0.16), rgba(255,255,255,1))" }}
                    >
                        <div className="flex items-center gap-3">
                            <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-moss/12 text-moss-deep">
                                <inv.icon size={16} />
                            </span>
                            <div className="min-w-0 flex-1">
                                <div className="scripture text-[17px] text-ink leading-snug truncate">
                                    {inv.label}
                                </div>
                                <div className="font-ui text-[12px] text-ink-70 mt-0.5">{inv.cta}</div>
                            </div>
                            <ArrowRight size={14} className="text-ink-40 shrink-0" />
                        </div>
                    </Link>
                ))}
            </div>
        </section>
    );
}

// ---------- section: continue reading ----------

function ContinueReading({ goals }) {
    const readingGoal = pickReadingGoal(goals);
    const chapter = readerLastPosition();
    const version = readerVersion();

    if (readingGoal) {
        return (
            <Link
                to="/read"
                data-testid="continue-reading"
                data-mode="reading-goal"
                className="block p-4 rounded-3xl bg-white border border-ink-10 hover:border-moss/40 transition mb-6"
            >
                <div className="flex items-center gap-3">
                    <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-moss/12 text-moss-deep">
                        <BookOpen size={16} />
                    </span>
                    <div className="min-w-0 flex-1">
                        <div className="font-ui text-[10px] uppercase tracking-[0.22em] text-ink-40 font-semibold">
                            Reading plan
                        </div>
                        <div className="scripture text-[17px] text-ink leading-snug">
                            {readingGoal.title}
                        </div>
                        <div className="font-ui text-[12px] text-ink-70 mt-0.5">
                            You're in Matthew {chapter}
                            {readingGoal.target_date ? ` · ${formatHorizon(readingGoal.target_date)}` : ""}
                        </div>
                    </div>
                    <ChevronRight size={16} className="text-ink-40 shrink-0" />
                </div>
            </Link>
        );
    }

    return (
        <Link
            to="/read"
            data-testid="continue-reading"
            data-mode="last-position"
            className="block p-4 rounded-3xl bg-white border border-ink-10 hover:border-moss/40 transition mb-6"
        >
            <div className="flex items-center gap-3">
                <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-moss/12 text-moss-deep">
                    <BookOpen size={16} />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="font-ui text-[10px] uppercase tracking-[0.22em] text-ink-40 font-semibold">
                        Continue reading
                    </div>
                    <div className="scripture text-[17px] text-ink leading-snug">
                        Matthew {chapter} <span className="text-ink-40">· {version.toUpperCase()}</span>
                    </div>
                    <div className="font-ui text-[12px] italic text-ink-40 mt-0.5">
                        Your reading place is kept here.
                    </div>
                </div>
                <ChevronRight size={16} className="text-ink-40 shrink-0" />
            </div>
        </Link>
    );
}

// ---------- section: doorways ----------

function nextGoalOccurrenceLine(goals) {
    for (const g of goals) {
        if (g.status !== "active") continue;
        const cfg = legacyRhythmConfig(g);
        const slots = scheduledSlotsForToday(cfg);
        if (slots.length) {
            const s = slots[0];
            return `Today · ${s.label || fmtTime(s.time)} · ${g.title}`;
        }
        if (g.target_date) {
            const d = daysUntil(g.target_date);
            if (d !== null && d >= 0) return `${g.title} · ${formatHorizon(g.target_date)}`;
        }
    }
    return null;
}

function Doorways({ goals, prayerStats }) {
    const goalLine = nextGoalOccurrenceLine(goals);
    const tiles = [
        {
            to: "/journal", key: "journal", label: "Journal", icon: PenLine,
            hint: "Type @ to add Scripture",
        },
        {
            to: "/notes", key: "notes", label: "Notes", icon: StickyNote,
            hint: "A place for what you're studying",
        },
        {
            to: "/prayer", key: "prayer", label: "Praying For", icon: Heart,
            accent: "plum",
            hint: prayerStats
                ? `${prayerStats.people} ${prayerStats.people === 1 ? "person" : "people"}${prayerStats.answered ? ` · ${prayerStats.answered} answered` : ""}`
                : "Names you're carrying to Him",
        },
        {
            to: "/goals", key: "goals", label: "Goals", icon: Target,
            hint: goalLine || "What you're working toward",
        },
        {
            to: "/read", key: "questions", label: "Questions", icon: HelpCircle,
            hint: "Explore the questions Scripture asks",
        },
    ];
    return (
        <section data-testid="home-doorways" className="mb-6">
            <h2 className="font-ui text-[10px] uppercase tracking-[0.22em] text-ink-40 font-semibold mb-2">
                Doorways
            </h2>
            <div className="grid grid-cols-2 gap-2.5">
                {tiles.map(t => (
                    <Link
                        key={t.key}
                        to={t.to}
                        data-testid={`doorway-${t.key}`}
                        className="p-3.5 rounded-2xl bg-white border border-ink-10 hover:border-moss/40 hover:shadow-sm transition flex flex-col gap-1.5 group"
                    >
                        <div className="flex items-center gap-2">
                            <span
                                className={
                                    "inline-flex items-center justify-center w-8 h-8 rounded-full " +
                                    (t.accent === "plum"
                                        ? "bg-plum/12 text-plum"
                                        : "bg-moss/12 text-moss-deep")
                                }
                            >
                                <t.icon size={14} />
                            </span>
                            <span className={"font-ui text-[13px] font-medium " + (t.accent === "plum" ? "text-plum" : "text-ink")}>
                                {t.label}
                            </span>
                            <ChevronRight size={13} className="ml-auto text-ink-40 group-hover:text-moss-deep transition" />
                        </div>
                        {t.hint && (
                            <p className={"font-ui text-[11px] leading-snug " + (t.accent === "plum" ? "text-plum" : "text-ink-70")}>
                                {t.hint}
                            </p>
                        )}
                    </Link>
                ))}
            </div>
        </section>
    );
}

// ---------- section: question of the day ----------

function QuestionOfTheDay() {
    const nav = useNavigate();
    const [q, setQ] = useState(null);

    useEffect(() => {
        fetchQuestionOfTheDay()
            .then(d => setQ(d.question || null))
            .catch(() => setQ(null));
    }, []);

    if (!q) return null;

    const reflect = () => {
        try {
            const uid = `n_${Math.random().toString(36).slice(2, 10)}`;
            sessionStorage.setItem("pending-question-card", JSON.stringify({
                node_id: uid,
                reference: `${q.book}.${q.chapter}.${q.verse}`,
                refs: [`${q.book}.${q.chapter}.${q.verse}`],
                label: `Matthew ${q.chapter}:${q.verse}`,
                translation: "kjv",
                text: q.question,
                format: "full",
            }));
        } catch (_e) { /* ignore */ }
        nav("/journal/new?fromQuestion=1");
    };

    return (
        <section data-testid="home-question" className="mb-8">
            <h2 className="font-ui text-[10px] uppercase tracking-[0.22em] text-ink-40 font-semibold mb-2">
                Question of the day
            </h2>
            <article className="p-5 rounded-3xl bg-white border border-ink-10 shadow-sm">
                <div className="font-ui text-[10px] uppercase tracking-[0.22em] text-plum font-semibold mb-2">
                    Matthew {q.chapter}:{q.verse}
                    {q.asked_by ? <span className="text-ink-40 normal-case tracking-normal ml-2 italic">— {q.asked_by}</span> : null}
                </div>
                <blockquote className="scripture text-[22px] md:text-[24px] leading-snug text-ink">
                    "{q.question}"
                </blockquote>
                <button
                    data-testid="question-reflect"
                    onClick={reflect}
                    className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-moss text-white font-ui text-[13px] font-medium hover:bg-moss-deep transition"
                >
                    <PenLine size={14} /> Reflect on this
                </button>
            </article>
        </section>
    );
}

// ---------- page ----------

export default function HomePage() {
    const [goals, setGoals] = useState([]);
    const [goalsLoaded, setGoalsLoaded] = useState(false);
    const [prayerStats, setPrayerStats] = useState(null);

    useEffect(() => {
        listGoals()
            .then(d => setGoals(d.goals || []))
            .catch(() => setGoals([]))
            .finally(() => setGoalsLoaded(true));
        // Prayer counts: /prayer-subjects returns {pinned, others} for active and
        // {grouped:{answered,laid_down,manual}} for kept. Sum the shapes correctly.
        Promise.all([
            listSubjects({ shelf: "active" }).catch(() => ({})),
            listSubjects({ shelf: "kept" }).catch(() => ({})),
        ]).then(([act, kept]) => {
            const activeCount = (act.pinned || []).length + (act.others || []).length;
            const answeredCount = ((kept.grouped || {}).answered || []).length;
            setPrayerStats({ people: activeCount, answered: answeredCount });
        });
    }, []);

    // Layout: mobile is a single column; desktop splits Continue (left) + Doorways (right),
    // with the greeting, invitations, and question of the day spanning full width.
    return (
        <div
            data-testid="home-page"
            className="px-5 md:px-8 py-6 md:py-8 pb-32 mx-auto w-full"
            style={{ maxWidth: "72ch" }}
        >
            <Greeting />
            <Invitations goals={goals} goalsLoaded={goalsLoaded} />
            <div className="lg:grid lg:grid-cols-5 lg:gap-6">
                <div className="lg:col-span-2">
                    <ContinueReading goals={goals} />
                </div>
                <div className="lg:col-span-3">
                    <Doorways goals={goals} prayerStats={prayerStats} />
                </div>
            </div>
            {/* Reserved slot for future community / testimony feed lives between doorways and question */}
            <QuestionOfTheDay />
        </div>
    );
}
